import React, { useRef, useState, useCallback } from "react";
import { Card as AntCard, type CardProps as AntCardProps } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import "./index.css";

export interface VideoCardProps extends Omit<AntCardProps, 'title' | 'children'> {
  /** 视频源地址 */
  src: string;
  /** 视频海报图片 */
  poster?: string;
  /** 卡片宽度 */
  width?: number | string;
  /** 卡片高度 */
  height?: number | string;
  /** 视频宽度 */
  videoWidth?: number | string;
  /** 视频高度 */
  videoHeight?: number | string;
  /** 是否显示播放按钮 */
  showPlayButton?: boolean;
  /** 是否自动播放（鼠标悬浮时） */
  autoPlayOnHover?: boolean;
  /** 点击事件 */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** 视频加载完成事件 */
  onVideoLoaded?: () => void;
  /** 视频播放事件 */
  onVideoPlay?: () => void;
  /** 视频暂停事件 */
  onVideoPause?: () => void;
  /** 视频结束事件 */
  onVideoEnded?: () => void;
  /** 自定义样式类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<VideoCardProps> & { __docgenInfo?: DocgenInfo };

/** 视频卡片组件，支持鼠标悬浮播放控制 */
export const VideoCard: React.FC<VideoCardProps> = ({
  src,
  poster,
  width = 300,
  height,
  videoWidth, // 参数保留但不使用，由CSS控制
  videoHeight, // 参数保留但不使用，由CSS控制
  showPlayButton = true,
  autoPlayOnHover = true,
  onClick,
  onVideoLoaded,
  onVideoPlay,
  onVideoPause,
  onVideoEnded,
  className,
  style,
  ...rest
}) => {
  // 忽略videoWidth和videoHeight，video尺寸完全由CSS控制
  void videoWidth;
  void videoHeight;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // 播放视频
  const playVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      onVideoPlay?.();
    }
  }, [onVideoPlay]);

  // 暂停视频
  const pauseVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      onVideoPause?.();
    }
  }, [onVideoPause]);

  // 切换播放状态
  const togglePlay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  }, [isPlaying, playVideo, pauseVideo]);

  // 鼠标进入
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (autoPlayOnHover && !isPlaying) {
      playVideo();
    }
  }, [autoPlayOnHover, isPlaying, playVideo]);

  // 鼠标离开
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (autoPlayOnHover && isPlaying) {
      pauseVideo();
    }
  }, [autoPlayOnHover, isPlaying, pauseVideo]);

  // 视频加载完成
  const handleVideoLoaded = useCallback(() => {
    setIsLoaded(true);
    onVideoLoaded?.();
  }, [onVideoLoaded]);

  // 视频播放事件
  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    onVideoPlay?.();
  }, [onVideoPlay]);

  // 视频暂停事件
  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
    onVideoPause?.();
  }, [onVideoPause]);

  // 视频结束事件
  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    onVideoEnded?.();
  }, [onVideoEnded]);

  // 卡片点击事件
  const handleCardClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
  }, [onClick]);

  return (
    <AntCard
      {...rest}
      className={`video-card ${className || ''}`}
      style={{
        width,
        height,
        padding: 0,
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        border: 'none',
        borderRadius: 0,
        ...style
      }}
      bordered={false}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleCardClick}
    >
      <div className="video-card-container">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          preload="metadata"
          onLoadedData={handleVideoLoaded}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnded}
          muted={true}
          loop={true}
        />
        
        {/* 播放/暂停按钮 */}
        {showPlayButton && (
          <div 
            className={`video-play-button ${isHovered ? 'hovered' : ''}`}
            onClick={togglePlay}
          >
            {isPlaying ? (
              <PauseCircleOutlined className="play-icon" />
            ) : (
              <PlayCircleOutlined className="play-icon" />
            )}
          </div>
        )}

        {/* 加载遮罩 */}
        {!isLoaded && (
          <div className="video-loading-mask">
            <div className="loading-spinner" />
          </div>
        )}
      </div>
    </AntCard>
  );
};

// 手动添加 docgen 信息
(VideoCard as ComponentWithDocgen).__docgenInfo = {
  displayName: "VideoCard",
  description: "视频卡片组件，支持鼠标悬浮播放控制",
  props: {
    src: {
      name: "src",
      required: true,
      description: "视频源地址",
      type: { name: "string", raw: "string" }
    },
    poster: {
      name: "poster",
      required: false,
      description: "视频海报图片",
      type: { name: "string", raw: "string" }
    },
    width: {
      name: "width",
      required: false,
      description: "卡片宽度",
      type: { name: "number | string", raw: "number | string" },
      defaultValue: { value: "300" }
    },
    height: {
      name: "height",
      required: false,
      description: "卡片高度",
      type: { name: "number | string", raw: "number | string" }
    },
    videoWidth: {
      name: "videoWidth",
      required: false,
      description: "视频宽度",
      type: { name: "number | string", raw: "number | string" },
      defaultValue: { value: '"100%"' }
    },
    videoHeight: {
      name: "videoHeight",
      required: false,
      description: "视频高度",
      type: { name: "number | string", raw: "number | string" },
      defaultValue: { value: '"100%"' }
    },
    showPlayButton: {
      name: "showPlayButton",
      required: false,
      description: "是否显示播放按钮",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    autoPlayOnHover: {
      name: "autoPlayOnHover",
      required: false,
      description: "是否自动播放（鼠标悬浮时）",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    onClick: {
      name: "onClick",
      required: false,
      description: "点击事件",
      type: { name: "function", raw: "(event: React.MouseEvent<HTMLDivElement>) => void" }
    },
    onVideoLoaded: {
      name: "onVideoLoaded",
      required: false,
      description: "视频加载完成事件",
      type: { name: "function", raw: "() => void" }
    },
    onVideoPlay: {
      name: "onVideoPlay",
      required: false,
      description: "视频播放事件",
      type: { name: "function", raw: "() => void" }
    },
    onVideoPause: {
      name: "onVideoPause",
      required: false,
      description: "视频暂停事件",
      type: { name: "function", raw: "() => void" }
    },
    onVideoEnded: {
      name: "onVideoEnded",
      required: false,
      description: "视频结束事件",
      type: { name: "function", raw: "() => void" }
    },
    className: {
      name: "className",
      required: false,
      description: "自定义样式类名",
      type: { name: "string", raw: "string" }
    },
    style: {
      name: "style",
      required: false,
      description: "自定义样式",
      type: { name: "CSSProperties", raw: "React.CSSProperties" }
    }
  }
};
